import { useState, useEffect, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { StructureTreeExplorer } from './explorer/StructureTreeExplorer.tsx'
import {
  buildTree,
  type DefaultParents,
  type ExplorerNode,
  type TreeBuildItem,
  DEFAULT_TARGET_KEYS,
} from './explorer/types.ts'
import type { GroupType } from './types.ts'
import { GROUP_TYPE_LABELS, GROUP_TYPES } from './types.ts'

export type { DefaultParents }

interface StandardItem {
  id: string
  type: 'group' | 'user' | 'ou' | 'share' | 'gpo' | string
  name_template: string
  description?: string | null
  config?: {
    groupType?: string
    /** Parent-OU: entweder OU-UUID (neu) oder OU-Name (Altdaten → wird automatisch aufgeloest) */
    parent?: string
    /** Ziel-OU: entweder OU-UUID (neu) oder OU-Name (Altdaten → wird automatisch aufgeloest) */
    ou?: string
    driveLetter?: string
    relativePath?: string
  } | null
  sort_order?: number
}

// ─── Backward-compat: Name → UUID-Aufloesung ───────────────────────────────────

/**
 * Loest einen gespeicherten Wert zu einer OU-ID auf.
 * Wenn der Wert bereits eine gueltige ID ist → direkt verwenden.
 * Wenn nicht → als Name suchen (Altdaten-Migration).
 */
function resolveToId(nameOrId: string, ous: StandardItem[]): string | null {
  if (ous.some((o) => o.id === nameOrId)) return nameOrId
  const found = ous.find(
    (o) => o.name_template.trim().toLowerCase() === nameOrId.trim().toLowerCase(),
  )
  return found?.id ?? null
}

/** Konvertiert StandardItem[] → TreeBuildItem[] mit UUID-basierter Aufloesung */
function standardItemsToTreeItems(items: StandardItem[]): TreeBuildItem[] {
  const ous = items.filter((i) => i.type === 'ou')
  return items
    .filter((i) => i.type === 'ou' || i.type === 'group' || i.type === 'user')
    .map<TreeBuildItem>((item) => {
      if (item.type === 'ou') {
        const parentRef = item.config?.parent
        const parentId = parentRef ? resolveToId(parentRef, ous) : null
        return {
          id: item.id,
          kind: 'ou',
          name: item.name_template,
          description: item.description ?? undefined,
          parentId: parentId ?? null,
        }
      }
      const ouRef = item.config?.ou
      const ouId = ouRef ? (resolveToId(ouRef, ous) ?? undefined) : undefined
      return {
        id: item.id,
        kind: item.type as 'group' | 'user',
        name: item.name_template,
        description: item.description ?? undefined,
        ouId,
        groupType: item.config?.groupType,
      }
    })
}

/**
 * Loest defaultParents-Werte zu IDs auf (Altdaten koennen noch Namen enthalten).
 */
function resolveDefaultParents(dp: DefaultParents, ous: StandardItem[]): DefaultParents {
  const resolved: DefaultParents = {}
  for (const key of DEFAULT_TARGET_KEYS) {
    const val = dp[key]
    if (!val) continue
    const id = resolveToId(val, ous)
    if (id) resolved[key] = id
  }
  return resolved
}

// ─── EditItemModal ─────────────────────────────────────────────────────────────

function EditItemModal({
  node,
  item,
  ouItems,
  isSaving,
  onSave,
  onClose,
}: {
  node: ExplorerNode
  item: StandardItem
  ouItems: StandardItem[]
  isSaving: boolean
  onSave: (payload: object) => void
  onClose: () => void
}) {
  const [name, setName] = useState(node.name)
  const [description, setDescription] = useState(node.description ?? '')
  const [groupType, setGroupType] = useState(item.config?.groupType ?? '')
  const [ouId, setOuId] = useState<string>(() => {
    const ref = item.config?.ou
    if (!ref) return ''
    return resolveToId(ref, ouItems) ?? ref
  })
  const [parentId, setParentId] = useState<string>(() => {
    const ref = item.config?.parent
    if (!ref) return ''
    return resolveToId(ref, ouItems) ?? ref
  })

  const kindLabel = node.kind === 'ou' ? 'OU' : node.kind === 'group' ? 'Gruppe' : 'User'

  function handleSave() {
    const trimmedName = name.trim()
    if (!trimmedName) return
    const payload: Record<string, unknown> = {
      name_template: trimmedName,
      description: description.trim() || null,
    }
    if (node.kind === 'group') {
      payload.config = { ...(item.config ?? {}), groupType: groupType || undefined, ou: ouId || undefined }
    } else if (node.kind === 'user') {
      payload.config = { ...(item.config ?? {}), ou: ouId || undefined }
    } else if (node.kind === 'ou') {
      payload.config = { ...(item.config ?? {}), parent: parentId || undefined }
    }
    onSave(payload)
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Einstellungen: {kindLabel}</h2>
            <p className="text-sm text-gray-500 font-mono mt-0.5">{node.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 mt-0.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              placeholder="Keine Beschreibung"
            />
          </div>

          {node.kind === 'group' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gruppen-Typ</label>
              <select
                value={groupType}
                onChange={(e) => setGroupType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">– kein Typ –</option>
                {GROUP_TYPES.map((t) => (
                  <option key={t} value={t}>{t} – {GROUP_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          )}

          {(node.kind === 'group' || node.kind === 'user') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ziel-OU{' '}
                <span className="text-gray-400 font-normal">(leer = Standard verwenden)</span>
              </label>
              <select
                value={ouId}
                onChange={(e) => setOuId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">– Standard –</option>
                {ouItems.map((o) => (
                  <option key={o.id} value={o.id}>{o.name_template}</option>
                ))}
              </select>
            </div>
          )}

          {node.kind === 'ou' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent-OU{' '}
                <span className="text-gray-400 font-normal">(leer = Domain-Root)</span>
              </label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">– Domain-Root –</option>
                {ouItems
                  .filter((o) => o.id !== item.id)
                  .map((o) => (
                    <option key={o.id} value={o.id}>{o.name_template}</option>
                  ))}
              </select>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {isSaving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── GlobalStandardsExplorer ───────────────────────────────────────────────────

interface Props {
  items: StandardItem[]
  defaultParents: DefaultParents
  isLoading: boolean
  canEdit?: boolean
}

export function GlobalStandardsExplorer({ items, defaultParents, isLoading, canEdit = true }: Props) {
  const qc = useQueryClient()
  const [editModal, setEditModal] = useState<{ node: ExplorerNode; item: StandardItem } | null>(null)

  const ouItems = useMemo(() => items.filter((i) => i.type === 'ou'), [items])

  // Altdaten-Migration: defaultParents-Werte (Namen) → IDs
  const resolvedDefaults = useMemo(
    () => resolveDefaultParents(defaultParents, ouItems),
    [defaultParents, ouItems],
  )

  const treeItems = useMemo(() => standardItemsToTreeItems(items), [items])
  const nodes = useMemo(() => buildTree(treeItems, resolvedDefaults), [treeItems, resolvedDefaults])

  const deleteItem = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/it-structures/global/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['it-standard-items'] }),
  })

  const addItem = useMutation({
    mutationFn: (payload: object) => apiClient.post('/it-structures/global/items', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['it-standard-items'] }),
  })

  const updateItem = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: object }) =>
      apiClient.put(`/it-structures/global/items/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['it-standard-items'] }),
  })

  const saveDefaults = useMutation({
    mutationFn: (next: DefaultParents) =>
      apiClient.put('/it-structures/global', { defaultParents: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['it-global-standards'] }),
  })

  // ─── Action Callbacks ────────────────────────────────────────────────────────

  function handleAddOu(parentId: string | null) {
    const name = window.prompt('Name der Standard-OU:')?.trim()
    if (!name) return
    addItem.mutate({
      type: 'ou',
      name_template: name,
      config: parentId ? { parent: parentId } : undefined,
      sort_order: items.length,
    })
  }

  function handleAddGroup(ouId: string, groupType: GroupType) {
    const suffix = window.prompt(`Neue ${groupType}-Gruppe — Name-Suffix:`)?.trim()
    if (!suffix) return
    addItem.mutate({
      type: 'group',
      name_template: `{PREFIX}_${groupType}_${suffix}`,
      config: { groupType, ou: ouId },
      sort_order: items.length,
    })
  }

  function handleAddUser(ouId: string) {
    const name = window.prompt('Neuer User — Template:', '{PREFIX}_admin')?.trim()
    if (!name) return
    addItem.mutate({
      type: 'user',
      name_template: name,
      config: { ou: ouId },
      sort_order: items.length,
    })
  }

  function handleRename(node: ExplorerNode) {
    const name = window.prompt('Neuer Name:', node.name)?.trim()
    if (!name || name === node.name) return
    updateItem.mutate({ id: node.id, payload: { name_template: name } })
  }

  function handleEditDescription(node: ExplorerNode) {
    const current = node.description ?? ''
    const next = window.prompt(`Beschreibung für „${node.name}":`, current)
    if (next === null || next.trim() === current) return
    updateItem.mutate({ id: node.id, payload: { description: next.trim() } })
  }

  function handleDelete(node: ExplorerNode) {
    const label = node.kind === 'ou' ? 'OU' : node.kind === 'group' ? 'Gruppe' : 'User'
    if (!window.confirm(`${label} „${node.name}" löschen?`)) return
    deleteItem.mutate(node.id)
  }

  function handleToggleDefault(ouId: string, target: keyof DefaultParents) {
    const next: DefaultParents = { ...resolvedDefaults }
    if (next[target] === ouId) {
      delete next[target]
    } else {
      next[target] = ouId
    }
    saveDefaults.mutate(next)
  }

  function handleDoubleClick(node: ExplorerNode) {
    const item = items.find((i) => i.id === node.id)
    if (!item) return
    setEditModal({ node, item })
  }

  const ouCount = items.filter((i) => i.type === 'ou').length
  const groupCount = items.filter((i) => i.type === 'group').length
  const userCount = items.filter((i) => i.type === 'user').length

  return (
    <>
      <StructureTreeExplorer
        nodes={nodes}
        domainLabel="DC=domain,DC=local"
        domainSubLabel="Domain-Root (Vorlage)"
        title="Standards-Explorer"
        ouCount={ouCount}
        groupCount={groupCount}
        userCount={userCount}
        isLoading={isLoading}
        emptyMessage="Noch keine Standards definiert. Rechtsklick auf den Template-Root, um eine OU anzulegen."
        hint="Doppelklick zum Bearbeiten · Rechtsklick für Optionen"
        actions={{
          canEdit,
          onAddOu: handleAddOu,
          onAddGroup: handleAddGroup,
          onAddUser: handleAddUser,
          onRename: handleRename,
          onEditDescription: handleEditDescription,
          onDelete: handleDelete,
          onDoubleClick: handleDoubleClick,
          defaultParents: resolvedDefaults,
          onToggleDefault: handleToggleDefault,
        }}
      />

      {editModal && (
        <EditItemModal
          node={editModal.node}
          item={editModal.item}
          ouItems={ouItems}
          isSaving={updateItem.isPending}
          onSave={(payload) => {
            updateItem.mutate(
              { id: editModal.node.id, payload },
              { onSuccess: () => setEditModal(null) },
            )
          }}
          onClose={() => setEditModal(null)}
        />
      )}
    </>
  )
}
